/**
 * RecipientInput - Chip + autocomplete input for email recipients.
 *
 * Features:
 * - Shows selected recipients as removable chips
 * - Autocomplete dropdown as user types (debounced 300ms)
 * - Keyboard navigation (Arrow keys, Enter, Backspace, Escape)
 * - Manual email entry: type an email and press Enter/comma/Tab to add, or blur the field
 * - Comma-separated paste support
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import portalService from '../../services/portal';
import { useClickOutside } from '../../hooks/useClickOutside';
import type { RecipientResult } from '../../types/portal';
import styles from './RecipientInput.module.css';

interface Recipient {
  email: string;
  name: string | null;
  company: string | null;
}

interface RecipientInputProps {
  /** Current list of recipients */
  recipients: Recipient[];
  /** Called when recipients change */
  onChange: (recipients: Recipient[]) => void;
  /** Placeholder text */
  placeholder?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  customer: 'customer',
  sales_account: 'account',
  ship_location: 'location',
  sales_rep: 'internal',
  email_history: 'email',
  sent_history: 'sent',
};

const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

export function recipientsToString(recipients: Recipient[]): string {
  return recipients.map((r) => r.email).join(', ');
}

/**
 * Parse an RFC 5322 address list ("A <a@x.com>, \"Doe, John\" <j@x.com>")
 * into recipients. Commas inside quoted display names or angle brackets are
 * NOT separators — naive splitting fragments such addresses into invalid
 * chips whose join Gmail rejects. The chip `email` always holds the bare
 * address; the display name goes to `name`.
 */
export function stringToRecipients(str: string): Recipient[] {
  if (!str.trim()) return [];

  const parts: string[] = [];
  let current = '';
  let inQuotes = false;
  let inAngle = false;
  for (const ch of str) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (ch === '<' && !inQuotes) inAngle = true;
    else if (ch === '>' && !inQuotes) inAngle = false;
    if (ch === ',' && !inQuotes && !inAngle) {
      parts.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  parts.push(current);

  return parts
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .map((part) => {
      const match = part.match(/^(.*?)\s*<([^<>]+)>$/);
      if (match) {
        const name = match[1].replace(/^"|"$/g, '').trim() || null;
        return { email: match[2].trim(), name, company: null };
      }
      return { email: part, name: null, company: null };
    });
}

export default function RecipientInput({
  recipients,
  onChange,
  placeholder = 'Search contacts or type email...',
}: RecipientInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<RecipientResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [invalidText, setInvalidText] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = inputValue.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await portalService.searchRecipients(trimmed);
        // Filter out already-selected emails
        const selectedEmails = new Set(recipients.map((r) => r.email.toLowerCase()));
        const filtered = results.filter(
          (r) => !selectedEmails.has(r.email.toLowerCase()),
        );
        setSuggestions(filtered);
        setIsOpen(filtered.length > 0);
        setHighlightIndex(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, recipients]);

  const closeDropdown = useCallback(() => setIsOpen(false), []);
  useClickOutside(wrapperRef, closeDropdown);

  const addRecipient = useCallback(
    (recipient: Recipient) => {
      // Deduplicate
      if (recipients.some((r) => r.email.toLowerCase() === recipient.email.toLowerCase())) {
        return;
      }
      onChange([...recipients, recipient]);
      setInputValue('');
      setSuggestions([]);
      setIsOpen(false);
      inputRef.current?.focus();
    },
    [recipients, onChange],
  );

  const removeRecipient = useCallback(
    (index: number) => {
      const next = [...recipients];
      next.splice(index, 1);
      onChange(next);
      inputRef.current?.focus();
    },
    [recipients, onChange],
  );

  const handleSelect = useCallback(
    (result: RecipientResult) => {
      addRecipient({
        email: result.email,
        name: result.name,
        company: result.company,
      });
    },
    [addRecipient],
  );

  const tryAddManualEmail = useCallback(
    (text: string) => {
      let raw = text.trim();
      let name: string | null = null;
      // Accept pasted "Name <addr@x.com>" forms, not just bare addresses
      const angleMatch = raw.match(/^(.*?)\s*<([^<>]+)>$/);
      if (angleMatch) {
        name = angleMatch[1].replace(/^"|"$/g, '').trim() || null;
        raw = angleMatch[2].trim();
      }
      const email = raw.toLowerCase();
      if (EMAIL_RE.test(email)) {
        addRecipient({ email, name, company: null });
        setInvalidText(false);
        return true;
      }
      setInvalidText(true);
      return false;
    },
    [addRecipient],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isOpen && suggestions.length > 0) {
        switch (e.key) {
          case 'ArrowDown':
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev < suggestions.length - 1 ? prev + 1 : 0,
            );
            return;
          case 'ArrowUp':
            e.preventDefault();
            setHighlightIndex((prev) =>
              prev > 0 ? prev - 1 : suggestions.length - 1,
            );
            return;
          case 'Enter':
            e.preventDefault();
            if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
              handleSelect(suggestions[highlightIndex]);
            } else if (inputValue.trim()) {
              tryAddManualEmail(inputValue);
            }
            return;
          case 'Escape':
            setIsOpen(false);
            return;
        }
      }

      // When dropdown is closed or no suggestions
      switch (e.key) {
        case 'Enter':
        case 'Tab':
          if (inputValue.trim()) {
            e.preventDefault();
            tryAddManualEmail(inputValue);
          }
          break;
        case 'Backspace':
          if (!inputValue && recipients.length > 0) {
            removeRecipient(recipients.length - 1);
          }
          break;
      }
    },
    [isOpen, suggestions, highlightIndex, inputValue, recipients, handleSelect, tryAddManualEmail, removeRecipient],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInvalidText(false);
      // Auto-add on comma (but not while typing a display name like "Doe, J...")
      if (val.endsWith(',') && !val.includes('<') && !val.includes('"')) {
        const emailPart = val.slice(0, -1).trim();
        if (emailPart) {
          tryAddManualEmail(emailPart);
        }
        return;
      }
      setInputValue(val);
    },
    [tryAddManualEmail],
  );

  const handleBlur = useCallback(() => {
    if (inputValue.trim()) {
      tryAddManualEmail(inputValue);
    }
  }, [inputValue, tryAddManualEmail]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      const text = e.clipboardData.getData('text');
      if (text.includes(',') || text.includes(';')) {
        e.preventDefault();
        const parts = text.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
        for (const part of parts) {
          tryAddManualEmail(part);
        }
      }
    },
    [tryAddManualEmail],
  );

  const chipLabel = (r: Recipient) => {
    if (r.name) return `${r.name} <${r.email}>`;
    return r.email;
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div
        className={styles.chipContainer}
        onClick={() => inputRef.current?.focus()}
      >
        {recipients.map((r, i) => (
          <span key={`${r.email}-${i}`} className={styles.chip} title={r.email}>
            <span className={styles.chipLabel}>{chipLabel(r)}</span>
            <button
              className={styles.chipRemove}
              onClick={(e) => {
                e.stopPropagation();
                removeRecipient(i);
              }}
              type="button"
              aria-label={`Remove ${r.email}`}
              data-action="recipient_remove"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className={styles.input}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onPaste={handlePaste}
          placeholder={recipients.length === 0 ? placeholder : ''}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-invalid={invalidText}
          style={invalidText ? { color: '#c0392b' } : undefined}
        />
      </div>
      {invalidText && inputValue.trim() && (
        <div className={styles.invalidHint} role="alert">
          "{inputValue.trim()}" is not a valid email address
        </div>
      )}

      {isOpen && suggestions.length > 0 && (
        <div className={styles.dropdown} role="listbox">
          {suggestions.map((result, idx) => (
            <button
              key={`${result.email}-${idx}`}
              className={`${styles.option} ${idx === highlightIndex ? styles.optionHighlighted : ''}`}
              onClick={() => handleSelect(result)}
              role="option"
              aria-selected={idx === highlightIndex}
              type="button"
              // The shared recorder names a control by its label unless told
              // otherwise, and this label is the contact's name and email:
              // record the pick by family and the directory it came from,
              // never the person (kanban c2632a0b).
              data-action="recipient_pick"
              data-record-value="source"
              value={result.source}
            >
              <div className={styles.optionMain}>
                {result.name && (
                  <span className={styles.optionName}>{result.name}</span>
                )}
                <span className={styles.optionEmail}>{result.email}</span>
              </div>
              <div className={styles.optionMeta}>
                {result.company && (
                  <span className={styles.optionCompany}>{result.company}</span>
                )}
                <span className={styles.sourceBadge}>
                  {SOURCE_LABELS[result.source] || result.source}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {loading && inputValue.trim().length >= 2 && !isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.loading}>Searching contacts...</div>
        </div>
      )}
    </div>
  );
}
